import { useEffect, useState } from "preact/hooks";
import type { ModalProps } from "~/components/modal.tsx";
import Modal from "~/components/modal.tsx";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import Button from "~/components/button.tsx";
import useForm from "~/hooks/use-form.ts";
import type { FormValues } from "~/hooks/use-form.ts";
import UploadImageField from "~/components/forms/upload-image-field.tsx";
import TextareaField from "~/components/forms/textarea-field.tsx";
import { useMainRegionApiClient } from "~/hooks/region/context.tsx";
import uploadTempFile from "~/api/upload-temp-file.ts";
import FormErrors from "~/components/forms/form-errors.tsx";
import type {
	CreateTestimonialMutation,
	CreateTestimonialMutationVariables,
} from "./create-testimonial-mutation.api.generated.ts";
import createTestimonialMutation from "./create-testimonial-mutation.api.graphql";
import classes from "./testimonials-modal.module.css";

type TestimonialsModalProps = Pick<ModalProps, "id">;

function TestimonialsModal({ id }: TestimonialsModalProps) {
	const t = useTranslator();
	const [droppedFile, setDroppedFile] = useState<File>();
	const apiClient = useMainRegionApiClient();
	const [testimonialSuccess, setTestimonialSuccess] = useState(false);
	const [abortController] = useState(() => new AbortController());
	useEffect(() => {
		return () => {
			abortController.abort();
		};
	}, [abortController]);
	const form = useForm({
		async onSubmit(values: FormValues) {
			let image: File | undefined;
			if (droppedFile) {
				image = droppedFile;
			} else if (values.image instanceof File) {
				image = values.image;
			}

			if (!image) {
				throw new Error(t("Please select a file"));
			}

			const text = values.text as string;

			const tempImage = await uploadTempFile(
				apiClient,
				{
					fileName: image.name,
					contentType: image.type,
					content: new Uint8Array(await image.arrayBuffer()),
				},
				{ signal: abortController.signal },
			);
			if (abortController.signal.aborted) {
				return;
			}

			await apiClient.mutate<
				CreateTestimonialMutation,
				CreateTestimonialMutationVariables
			>(createTestimonialMutation, {
				text,
				tempImage,
			});
			setTestimonialSuccess(true);
		},
	});
	const { isWorking, formError, formProps } = form;

	return (
		<Modal size="small" id={id}>
			<div className={classes["container"]}>
				<h1 className="heading-1">{t("Testimonials")}</h1>
				<em>{t("Coming soon")}</em>
				<form {...formProps} className={classes["form"]}>
					<h2 className="heading-2">{t("Leave a testimonial")}</h2>
					{testimonialSuccess ? (
						<div className={classes["success"]}>
							{t("Thank you for sending your testimonial!")}
						</div>
					) : (
						<>
							<TextareaField
								name="text"
								rows={5}
								minLength={50}
								required
								disabled={isWorking}
							/>
							<UploadImageField
								name="image"
								label={t("Upload an image")}
								onDropFile={setDroppedFile}
								required
								disabled={isWorking}
							/>
							<FormErrors errors={[formError]} />
							<Button
								type="submit"
								variant="blue"
								working={isWorking}
								fullWidth
							>
								{t("Send")}
							</Button>
						</>
					)}
				</form>
			</div>
		</Modal>
	);
}

export default TestimonialsModal;
